import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Devices as DevicesIcon,
  BugReport as TicketIcon,
  LocalHospital as AlarmIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  People as FamilyIcon,
  Event as EventIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import ResponsiveTabsAccordion from '../../../../components/ResponsiveTabsAccordion';
import LocationDetailHeader from './LocationDetailHeader';
import LocationEmptyState from '../shared/LocationEmptyState';

// Tab components
import OverviewTab from './tabs/OverviewTab';
import DevicesTab from './tabs/DevicesTab';
import TicketsTab from './tabs/TicketsTab';
import AlarmsTab from './tabs/AlarmsTab';
import HistoryTab from './tabs/HistoryTab';
import PersonsTab from './tabs/PersonsTab';
import SettingsTab from './tabs/SettingsTab';
import FamilyLinksTab from './tabs/FamilyLinksTab';
import EventsTab from './tabs/EventsTab';
import PhotosTab from './tabs/PhotosTab';
import DomoticsTab from './tabs/DomoticsTab';

/**
 * Right-side detail panel for a selected location
 * Contains header + tabbed content
 */
const LocationDetailPanel = ({
  selectedLocation,
  selectedTab,
  setSelectedTab,
  // Data
  devices,
  loadingDevices,
  locationPersons,
  loadingPersons,
  enoceanButtons,
  loadingButtons,
  buttonError,
  setButtonError,
  hierarchicalLocations,
  // Expected devices
  expectedDevicesStatus,
  expectedDeviceTypes,
  expectedDevicesExpanded,
  setExpectedDevicesExpanded,
  savingExpectedDevices,
  allDeviceTypes,
  loadingDeviceTypes,
  handleAddExpectedDeviceType,
  handleRemoveExpectedDeviceType,
  handleUpdateMinCount,
  // Navigation
  siblingNavigation,
  onNavigateSibling,
  // Actions
  onSelectLocation,
  setSelectedLocation,
  setLocations,
  onAddLocation,
  onEditLocation,
  onDeleteLocation,
  onAddDevice,
  onEditDevice,
  onDeleteDevice,
  onDeleteButton,
  onOpenButtonTest,
  onRefresh,
  t
}) => {
  if (!selectedLocation) {
    return <LocationEmptyState type="no_selection" t={t} />;
  }

  const locationTabs = useMemo(() => [
    {
      label: 'Overview',
      icon: <DashboardIcon />,
      content: (
        <OverviewTab
          selectedLocation={selectedLocation}
          devices={devices}
          hierarchicalLocations={hierarchicalLocations}
          onSelectLocation={onSelectLocation}
          setSelectedLocation={setSelectedLocation}
          setLocations={setLocations}
          t={t}
        />
      )
    },
    {
      label: 'Devices',
      icon: <DevicesIcon />,
      content: (
        <DevicesTab
          selectedLocation={selectedLocation}
          devices={devices}
          loadingDevices={loadingDevices}
          onRefresh={() => onSelectLocation(selectedLocation)}
          onEditDevice={onEditDevice}
          onDeleteDevice={onDeleteDevice}
          onAddDevice={onAddDevice}
          expectedDevicesStatus={expectedDevicesStatus}
          expectedDeviceTypes={expectedDeviceTypes}
          expectedDevicesExpanded={expectedDevicesExpanded}
          setExpectedDevicesExpanded={setExpectedDevicesExpanded}
          savingExpectedDevices={savingExpectedDevices}
          allDeviceTypes={allDeviceTypes}
          loadingDeviceTypes={loadingDeviceTypes}
          handleAddExpectedDeviceType={handleAddExpectedDeviceType}
          handleRemoveExpectedDeviceType={handleRemoveExpectedDeviceType}
          handleUpdateMinCount={handleUpdateMinCount}
        />
      )
    },
    {
      label: 'Tickets',
      icon: <TicketIcon />,
      content: <TicketsTab selectedLocation={selectedLocation} />
    },
    {
      label: 'Alarms',
      icon: <AlarmIcon />,
      content: <AlarmsTab selectedLocation={selectedLocation} />
    },
    {
      label: 'History',
      icon: <HistoryIcon />,
      content: <HistoryTab selectedLocation={selectedLocation} />
    },
    {
      label: 'Persons',
      icon: <PersonIcon />,
      content: (
        <PersonsTab
          selectedLocation={selectedLocation}
          locationPersons={locationPersons}
          loadingPersons={loadingPersons}
        />
      )
    },
    {
      label: 'Settings',
      icon: <SettingsIcon />,
      content: (
        <SettingsTab
          selectedLocation={selectedLocation}
          enoceanButtons={enoceanButtons}
          loadingButtons={loadingButtons}
          buttonError={buttonError}
          setButtonError={setButtonError}
          onDeleteButton={onDeleteButton}
          onOpenButtonTest={onOpenButtonTest}
          t={t}
        />
      )
    },
    {
      label: 'Family Links',
      icon: <FamilyIcon />,
      content: <FamilyLinksTab selectedLocation={selectedLocation} />
    },
    {
      label: 'Events',
      icon: <EventIcon />,
      content: <EventsTab selectedLocation={selectedLocation} />
    },
    {
      label: 'Photos',
      icon: <PhotoLibraryIcon />,
      content: <PhotosTab selectedLocation={selectedLocation} />
    },
    {
      label: t ? t('LocationDetails.domotics') : 'Domotics',
      icon: <HomeIcon />,
      content: <DomoticsTab selectedLocation={selectedLocation} t={t} />
    }
  ], [
    selectedLocation, devices, loadingDevices, locationPersons, loadingPersons,
    enoceanButtons, loadingButtons, buttonError, hierarchicalLocations,
    expectedDevicesStatus, expectedDeviceTypes, expectedDevicesExpanded,
    savingExpectedDevices, allDeviceTypes, loadingDeviceTypes, t
  ]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <LocationDetailHeader
        selectedLocation={selectedLocation}
        devices={devices}
        siblingNavigation={siblingNavigation}
        onAddLocation={onAddLocation}
        onEditLocation={onEditLocation}
        onDeleteLocation={onDeleteLocation}
        onNavigateSibling={onNavigateSibling}
        onRefresh={onRefresh}
        t={t}
      />

      <ResponsiveTabsAccordion
        tabs={locationTabs}
        value={selectedTab}
        onChange={(e, v) => setSelectedTab(v)}
        defaultAllClosed={true}
      />
    </Box>
  );
};

export default LocationDetailPanel;
